import type {
  AnimalCategory,
  CategoriaAnimal,
  Cocho,
  Corral,
  Curral,
  FeedTrough,
  Module,
  Modulo,
  OperationType,
  Pasto,
  PastoCocho,
  Pasture,
  PastureFeedTrough,
  Retiro,
  Retreat,
  Objetivo,
  TipoMovimentacao,
  Goal,
} from "./types";

class TransformerUtils {
  toOperationType(data: TipoMovimentacao): OperationType;
  toOperationType(data: TipoMovimentacao | null): OperationType | null;
  toOperationType(data?: TipoMovimentacao | null): OperationType | null;
  toOperationType(data?: TipoMovimentacao | null): OperationType | null {
    if (data == null) return null;

    // current API only returns id and name, so we map only these fields
    return {
      createdAt: "",
      creationUser: "",
      deletedAt: null,
      deletionUser: null,
      id: data.IdTipoMovimentacao,
      name: data.DescricaoTipoMovimentacao,
      updatedAt: null,
      updateUser: null,
    };
  }

  toCategoriaAnimal(data: AnimalCategory): CategoriaAnimal;
  toCategoriaAnimal(data: AnimalCategory | null): CategoriaAnimal | null;
  toCategoriaAnimal(data?: AnimalCategory | null): CategoriaAnimal | null;
  toCategoriaAnimal(data?: AnimalCategory | null): CategoriaAnimal | null {
    if (data == null) return null;

    return {
      IdCategoriaAnimal: data.id,
      IdFazenda: data.farmId,
      IdFaixaIdade: data.ageRangeId,
      IdCurvaGanhoPeso: data.weightGainCurveId,
      Nome: data.name,
      Sexo: data.gender,
      Hash: data.hash,
      Alertas: data.warnings,
      TamanhoDesejado: data.desiredNumber,
      QuantidadeInicialParaConciliacao: data.initialNumberConference,
      UsuarioCriacao: data.creationUser,
      DataCriacao: data.createdAt,
      UsuarioAlteracao: data.updateUser,
      DataAlteracao: data.updatedAt,
      Excluido: !!(data.deletedAt || data.deletionUser),
      UsuarioExclusao: data.deletionUser,
      DataExclusao: data.deletedAt,
    };
  }

  toPastureFeedTrough(data: PastoCocho): PastureFeedTrough;
  toPastureFeedTrough(data: PastoCocho | null): PastureFeedTrough | null;
  toPastureFeedTrough(data?: PastoCocho | null): PastureFeedTrough | null;
  toPastureFeedTrough(data?: PastoCocho | null): PastureFeedTrough | null {
    if (data == null) return null;

    return {
      createdAt: data.DataCriacao,
      creationUser: data.UsuarioCriacao,
      deletedAt: data.DataExclusao,
      deletionUser: data.UsuarioExclusao,
      expirationDate: data.DataValidade,
      feedTroughId: data.IdCocho,
      id: data.IdPastoCocho,
      isDeleted: data.Excluido,
      pastureId: data.IdPasto,
      updatedAt: data.DataAlteracao,
      updateUser: data.UsuarioAlteracao,
    };
  }

  toFeedTrough(data: Cocho): FeedTrough;
  toFeedTrough(data: Cocho | null): FeedTrough | null;
  toFeedTrough(data?: Cocho | null): FeedTrough | null;
  toFeedTrough(data?: Cocho | null): FeedTrough | null {
    if (data == null) return null;

    return {
      color: data.Cor,
      createdAt: data.DataCriacao,
      creationUser: data.UsuarioCriacao,
      deletedAt: data.DataExclusao,
      deletionUser: data.UsuarioExclusao,
      farmId: data.IdFazenda,
      id: data.IdCocho,
      hasGoodAccess: data.BomAcesso,
      isCovered: data.Coberto,
      isCreepFeedTrough: data.CochoCreep,
      isDeleted: data.Excluido,
      isElevated: data.Elevado,
      isEndStock: data.EstoqueFinal,
      isShared: data.CochoCompartilhado,
      length: data.Comprimento,
      pastureId: data.IdPasto,
      updatedAt: data.DataAlteracao,
      updateUser: data.UsuarioAlteracao,
      pastureFeedTrough: this.toPastureFeedTrough(data.PastoCocho),
    };
  }

  toModule(data: Modulo): Module;
  toModule(data: Modulo | null): Module | null;
  toModule(data?: Modulo | null): Module | null;
  toModule(data?: Modulo | null): Module | null {
    if (data == null) return null;

    return {
      color: data.Cor,
      createdAt: data.DataCriacao,
      creationUser: data.UsuarioCriacao,
      deletedAt: data.DataExclusao,
      deletionUser: data.UsuarioExclusao,
      farmId: data.IdFazenda,
      id: data.IdModulo,
      isDeleted: data.Excluido,
      name: data.Nome,
      retreatId: data.IdRetiro,
      shortName: data.NomeReduzido,
      updatedAt: data.DataAlteracao,
      updateUser: data.UsuarioAlteracao,
    };
  }

  toPasture(data: Pasto): Pasture;
  toPasture(data: Pasto | null): Pasture | null;
  toPasture(data?: Pasto | null): Pasture | null;
  toPasture(data?: Pasto | null): Pasture | null {
    if (data == null) return null;

    return {
      coordinates: data.Coordenadas,
      createdAt: data.DataCriacao,
      creationUser: data.UsuarioCriacao,
      deletedAt: data.DataExclusao,
      deletionUser: data.UsuarioExclusao,
      effectiveArea: data.AreaEfetiva,
      farmId: data.IdFazenda,
      forageId: data.IdForragem,
      id: data.IdPasto,
      hash: data.Hash,
      hasConfinement: Boolean(data.TemConfinamento),
      isCorral: data.Curral,
      isDeleted: data.Excluido,
      moduleId: data.IdModulo,
      moduleOrder: data.OrdemModulo,
      name: data.Nome,
      openArea: data.AreaAberta,
      originFeedTroughId: data.IdCochoOrigem,
      pastureCenter: data.CentroPasto,
      primePastureId: data.IdPastoPrime,
      productionPotential: data.PotencialDeProducao,
      retreatId: data.IdRetiro,
      shortName: data.NomeReduzido,
      updatedAt: data.DataAlteracao,
      updateUser: data.UsuarioAlteracao,
      feedTrough: this.toFeedTrough(data.Cocho),
      module: this.toModule(data.Modulo),
      pastureFeedTroughs:
        data.PastoCochos?.map((item) => this.toFeedTrough(item)).filter(
          (item): item is FeedTrough => item != null
        ) ?? [],
    };
  }

  toCorral(data: Curral): Corral;
  toCorral(data: Curral | null): Corral | null;
  toCorral(data?: Curral | null): Corral | null;
  toCorral(data?: Curral | null): Corral | null {
    return this.toPasture(data) as Corral | null;
  }

  toRetreat(data: Retiro): Retreat;
  toRetreat(data: Retiro | null): Retreat | null;
  toRetreat(data?: Retiro | null): Retreat | null;
  toRetreat(data?: Retiro | null): Retreat | null {
    if (data == null) return null;

    return {
      createdAt: data.DataCriacao,
      creationUser: data.UsuarioCriacao,
      deletedAt: data.DataExclusao,
      deletionUser: data.UsuarioExclusao,
      farmId: data.IdFazenda,
      hash: data.Hash,
      id: data.IdRetiro,
      isDeleted: data.Excluido,
      name: data.Nome,
      shortName: data.NomeReduzido,
      updatedAt: data.DataAlteracao,
      updateUser: data.UsuarioAlteracao,
    };
  }

  toObjetivo(data: Goal): Objetivo;
  toObjetivo(data: Goal | null): Objetivo | null;
  toObjetivo(data?: Goal | null): Objetivo | null;
  toObjetivo(data?: Goal | null): Objetivo | null {
    if (data == null) return null;

    return {
      IdObjetivo: data.id,
      Nome: data.name,
      IdFazenda: data.farmId,
      UsuarioCriacao: data.creationUser,
      DataCriacao: data.createdAt,
      UsuarioAlteracao: data.updateUser,
      DataAlteracao: data.updatedAt,
      UsuarioExclusao: data.deletionUser,
      Excluido: data.deletedAt !== null,
      DataExclusao: data.deletedAt,
    };
  }
}

const transformerUtils = new TransformerUtils();
export default transformerUtils;
